import React from 'react';
import { Link } from '@/components/LocalizedLink';
import { ArrowRight, Package, Camera } from 'lucide-react';
import { motion } from 'framer-motion';
import { useApiData } from '@/hooks/useApiData';

const NO_ITEMS = [];
const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

export default function ProductsPage() {
  const { data: products, loading } = useApiData('/api/products', NO_ITEMS);

  return (
    <div data-testid="products-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-saffron-50/30" />
        <div className="relative max-w-7xl mx-auto">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <span className="text-xs uppercase tracking-[0.3em] text-saffron font-sans font-semibold">Product Catalogue</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-earth-dark mt-3 tracking-tight">
              Our Spice Powder Range
            </h1>
            <p className="mt-4 text-lg text-earth/60 font-sans max-w-xl leading-relaxed">
              Premium export-grade spice powders, finely ground. Each product comes with detailed specifications, lab certificates, and flexible MOQ options.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Products */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white rounded-lg p-8 animate-pulse border border-saffron-100/50">
                  <div className="h-48 bg-saffron-50 rounded-lg mb-6" />
                  <div className="h-6 bg-saffron-50 w-1/3 mb-3" />
                  <div className="h-4 bg-saffron-50 w-2/3" />
                </div>
              ))}
            </div>
          ) : (
            products.map((product, i) => (
              <motion.div
                key={product.id}
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={fadeUp} transition={{ delay: i * 0.1 }}
              >
                <div
                  className="bg-white rounded-lg overflow-hidden shadow-card-lift hover:shadow-card-hover transition-all border border-saffron-100/50 group"
                  data-testid={`product-row-${product.id}`}
                >
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
                    {/* Image */}
                    <div className="relative aspect-[16/10] lg:aspect-auto overflow-hidden">
                      {product.image ? (
                        <img src={product.image} alt={`${product.name} - Export quality Indian spice powder from ASJK Laxmi Global`} width={800} height={600} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-saffron-50/60 to-cream flex items-center justify-center min-h-[200px]">
                          <Camera size={36} className="text-earth/15" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/10" />
                    </div>

                    {/* Content */}
                    <div className="lg:col-span-2 p-6 lg:p-8 flex flex-col justify-between">
                      <div>
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h2 className="font-display text-2xl sm:text-3xl font-bold text-earth-dark tracking-tight">{product.name}</h2>
                            <p className="text-sm text-saffron font-sans font-medium mt-1">{product.tagline}</p>
                          </div>
                          <div className="w-10 h-10 rounded-full flex-shrink-0" style={{ backgroundColor: product.color + '30', border: `2px solid ${product.color}50` }}>
                            <div className="w-full h-full rounded-full" style={{ backgroundColor: product.color, opacity: 0.4 }} />
                          </div>
                        </div>
                        <p className="mt-4 text-sm text-earth/60 font-sans leading-relaxed">{product.description}</p>

                        {/* Specs grid */}
                        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
                          {[
                            { label: 'Grade', value: product.specs.grade },
                            { label: 'Form', value: Array.isArray(product.specs.form) ? product.specs.form.join(', ') : product.specs.form },
                            { label: 'Moisture', value: product.specs.moisture },
                            { label: 'MOQ', value: product.specs.moq },
                          ].map(spec => (
                            <div key={spec.label} className="bg-saffron-50/50 p-3 rounded">
                              <p className="text-[10px] uppercase tracking-wider text-saffron font-sans font-semibold">{spec.label}</p>
                              <p className="text-xs text-earth-dark font-sans mt-0.5 font-medium">{spec.value}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="mt-6 flex flex-wrap gap-3">
                        <Link
                          to={`/products/${product.id}`}
                          className="inline-flex items-center gap-2 bg-saffron text-white font-sans font-semibold text-xs px-6 py-2.5 rounded-md hover:bg-saffron-dark transition-colors"
                          data-testid={`view-${product.id}`}
                        >
                          Full Details <ArrowRight size={14} />
                        </Link>
                        <Link
                          to={`/contact?product=${encodeURIComponent(product.name)}`}
                          className="inline-flex items-center gap-2 border border-earth/20 text-earth-dark font-sans font-semibold text-xs px-6 py-2.5 rounded-md hover:bg-earth/5 transition-colors"
                          data-testid={`quote-${product.id}`}
                        >
                          <Package size={14} /> Request Quote
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-earth-dark text-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-white tracking-tight">Need Custom Grades or Specifications?</h2>
          <p className="mt-2 text-cream-muted/60 font-sans">We offer custom processing, blending, and packaging to match your exact requirements.</p>
          <Link to="/contact" className="mt-6 inline-flex items-center gap-2 bg-saffron text-white font-sans font-semibold text-sm px-8 py-3.5 rounded-md hover:bg-saffron-dark transition-colors" data-testid="products-cta">
            Get Custom Quote <ArrowRight size={16} />
          </Link>
        </motion.div>
      </section>
    </div>
  );
}
